import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { provideLtPrimeNg } from './third-party/lt-primeng.providers';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IPublicClientApplication, PublicClientApplication, InteractionType, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';
import { msalConfig, loginRequest, protectedResources } from './auth-config';
import { MonacoEditorModule } from '@materia-ui/ngx-monaco-editor';
import { ClientIntegrationService } from './services/client-integration.service';
import { FastFetchService } from './services/fastfetch.service';
import { IdentifierService } from './services/identifier.service';
import { MetricService } from './services/metric.service';
import { ReportService } from './services/report.service';
import { ServerService } from './services/server.service';
import { BaseMappingService } from './services/base-mapping.service';
import { DefaultConfigPlaceholderService } from './services/default-config-placeholder.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './components/home/home.component';
import { ErrorComponent } from './components/error/error.component';
import { UserComponent } from './components/user/user.component';
import { NavMenuComponent } from './components/nav-menu/nav-menu.component';
import { FooterComponent } from './components/footer/footer.component';
import { ClientsComponent } from './components/clients/clients.component';
import { EditConfigComponent } from './components/edit-config/edit-config.component';
import { IntegrationRunTimeComponent } from './components/integration-runtime/integration-runtime.component';
import { ErrorMessagesV3Component } from './components/error-messages-v3/error-messages-v3';
import { AddIntegrationIdentifierComponent } from './components/add-integration-identifier/add-integration-identifier.component';
import { NotfoundComponent } from './components/notfound/notfound.component';
import { ExcelPasteDirective } from './directives/excel-paste.directive';
import { ReportsComponent } from './components/reports/reports.component';
import { ReportViewComponent } from './components/report-view/report-view.component';
import { IntegrationReportViewComponent } from './components/report-view/integration-report-view/integration-report-view.component';
import { UndeleteComponent } from './components/report-view/undelete/undelete.component';
import { ToastModule } from './components/toast-message/toast.module';
import { RoleGuard } from './guards/role-guard';
import { EditConfigGuard } from './guards/edit-config-guard';
import { EditGenericEndpointsGuard } from './guards/edit-generic-endpoints-guard';
import { EditDefaultConfigGuard } from './guards/edit-default-config-guard';
import { IntegrationRevisionComponent } from './components/integration-revision/integration-revision.component';
import { AdminDashboardComponent } from './components/admin-dashboard/admin-dashboard.component';
import { IdentifierViewComponent } from './components/identifier-view/identifier-view.component';
import { DefaultConfigsComponent } from './components/default-configs/default-configs.component';
import { EditDefaultConfigsComponent } from './components/edit-default-configs/edit-default-configs.component';
import { AddDefaultConfigsComponent } from './components/add-default-configs/add-default-configs.component';
import { CheckInListDirective } from './directives/check-in-list.directive';
import { CheckNotInListDirective } from './directives/check-not-in-list.directive';
import { EditBaseMappingGuard } from './guards/edit-base-mapping-guard';
import { ClientConfigurationSecretsComponent } from './components/client-configuration-secrets/client-configuration-secrets.component';
import { ClientConfigurationSecretService } from './services/client-configuration-secret.service';
import { TypeConstantService } from './services/type-constant.service';
import { InvoicingService } from './services/invoicing.service';
import { FieldPathService } from './services/field-path.service';
import { ClientInformationService } from './services/client-information.service';
import { ClientContractService } from './services/client-contract.service';
import { FieldPathsViewComponent } from './components/field-paths-view/field-paths-view.component';
import { LOSTalkerTitleService } from './services/lostalker-title-service.service';
import { IntegrationRunTimeService } from './services/integration-runtime.service';
import { IntegrationPushBackService } from './services/integration-pushback.service';
import { ClientContractReportComponent } from './components/report-view/client-contract-report/client-contract-report.component';
//import { SwaggerInterfaceComponent } from './components/swagger-interface/swagger-interface.component';
import { SwaggerInterfaceV2Component } from './components/swagger-interface-v2/swagger-interface-v2.component';
import { LosTokenViewComponent } from './components/los-token-view/los-token-view.component';
import { LosTokenService } from './services/los-token.service';
import { LosTokenMappingViewComponent } from './components/los-token-mapping-view/los-token-mapping-view.component';
import { SQLQueriesComponent } from './components/sql-queries/sql-queries.component';
import { EditSQLQueryComponent } from './components/edit-sql-query/edit-sql-query.component';
import { CustomSQLQueryService } from './services/sql-queries.service';
import { IntegrationMaintenanceService } from './services/integration-maintenance.service';
import { AddSQLQueryComponent } from './components/add-sql-query/add-sql-query.component';
import { IntegrationinfoReportViewComponent } from './components/report-view/integrationinfo-report-view/integrationinfo-report-view.component';
import { ChangeLogsComponent } from './components/changelogs/changelogs.component';
import { IntegrationBulkReportComponent } from './components/report-view/integration-bulk-report/integration-bulk-report.component';
import { FailedLoansViewComponent } from './components/failed-loans-view/failed-loans-view.component';
import { MappingsReportViewV2Component } from './components/report-view/mappings-report-view-v2/mappings-report-view-v2.component';
import { IntegrationErrorReportViewV3Component } from './components/report-view/integration-error-report-view-v3/integration-error-report-view-v3';
import { IntegrationMaintenanceComponent } from './components/integration-maintenance/integration-maintenance.component';
import { EditIntegrationMaintenanceComponent } from './components/edit-integration-maintenance/edit-integration-maintenance.component';
import { ClientPushbacksComponent } from './components/client-pushbacks/client-pushbacks.component';
import { MatListModule } from '@angular/material/list';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { SharedModule } from './shared/shared.module';
import { LTGridModule } from './features/grid/grid.module';
const _isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1; // Remove this line to use Angular Universal
import { LTPrimeNgModule } from './third-party/lt-primeng.module';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {CdkListboxModule} from '@angular/cdk/listbox';

import { MatFormFieldModule } from '@angular/material/form-field';
import { LTPushBackModule } from './features/lt-pushback/lt-pushback.module';
import { LOSDataSourceReportComponent } from './components/report-view/los-data-source-report/los-data-source-report.component';
import { LTSharedUIModule } from './features/lt-shared-ui/lt-shared-ui.module';
import { LTInputModule } from './features/lt-input/lt-input.module';
import { LTErrorsModule } from './features/lt-errors/lt-errors.module';
import { LTFilterModule } from './features/filter/lt-filter.module';
import { LTEditConfigModule } from './features/lt-edit-config/lt-edit-config.module';

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();

  protectedResourceMap.set(protectedResources.LOSTalkerAPI.endpoint, protectedResources.LOSTalkerAPI.scopes);
  protectedResourceMap.set(protectedResources.APISpecPage.endpoint, protectedResources.APISpecPage.scopes);

  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Popup,
    authRequest: loginRequest,
  };
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    ErrorComponent,
    NavMenuComponent,
    FooterComponent,
    ClientsComponent,
    EditConfigComponent,
    ErrorMessagesV3Component,
    AddIntegrationIdentifierComponent,
    ExcelPasteDirective,
    NotfoundComponent,
    ReportsComponent,
    ReportViewComponent,
    IntegrationReportViewComponent,
    UndeleteComponent,
    UserComponent,
    IntegrationRevisionComponent,
    AdminDashboardComponent,
    IdentifierViewComponent,
    DefaultConfigsComponent,
    EditDefaultConfigsComponent,
    AddDefaultConfigsComponent,
    CheckInListDirective,
    CheckNotInListDirective,
    ClientConfigurationSecretsComponent,
    FieldPathsViewComponent,
    ClientContractReportComponent,
    //SwaggerInterfaceComponent,
    SwaggerInterfaceV2Component,
    IntegrationRunTimeComponent,
    LosTokenViewComponent,
    LosTokenMappingViewComponent,
    SQLQueriesComponent,
    EditSQLQueryComponent,
    AddSQLQueryComponent,
    IntegrationinfoReportViewComponent,
    ChangeLogsComponent,
    IntegrationBulkReportComponent,
    FailedLoansViewComponent,
    MappingsReportViewV2Component,
    IntegrationErrorReportViewV3Component,
    ClientPushbacksComponent,
    IntegrationMaintenanceComponent,
    EditIntegrationMaintenanceComponent,
    LOSDataSourceReportComponent,
  ],
  imports: [
    BrowserModule,
    FormsModule,
    AppRoutingModule,
    HttpClientModule,
    MatGridListModule,
    MonacoEditorModule,
    MsalModule,
    ToastModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatDialogModule,
    // MatDialogActions,
    // MatDialogClose,
    MatSelectModule,
    MatAutocompleteModule,
    MatIconModule,
    MatCardModule,
    MatCheckboxModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatTooltipModule,
    MatTabsModule,
    MatButtonToggleModule,
    MatButtonModule,
    MatChipsModule,
    MatDividerModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatExpansionModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatListModule,
    MatToolbarModule,
    ClipboardModule,
    SharedModule,
    LTGridModule,
    LTPrimeNgModule,
    DragDropModule,
    CdkListboxModule,
    LTPushBackModule,
    LTSharedUIModule,
    LTInputModule,
    LTErrorsModule,
    LTFilterModule,
    LTEditConfigModule,
    // LTAddModule, LTServerModule, LTEndpointModule are lazy-loaded via app-routing.module.ts
  ],
  providers: [
    // PrimeNG + animations singletons, registered ONCE at the app environment injector.
    // Config lives in third-party/lt-primeng.providers.ts (co-located with LTPrimeNgModule).
    provideLtPrimeNg(),
    RoleGuard,
    { provide: 'ORIGIN_URL', useValue: location.origin },
    // {
    //   provide: MONACO_PATH,
    //   useValue: 'https://unpkg.com/browse/monaco-editor@0.21.3/min/vs/'
    // },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalGuard,
    MsalBroadcastService,
    MsalService,
    ClientIntegrationService,
    ClientConfigurationSecretService,
    BaseMappingService,
    IdentifierService,
    MetricService,
    ReportService,
    FastFetchService,
    ServerService,
    DefaultConfigPlaceholderService,
    TypeConstantService,
    InvoicingService,
    FieldPathService,
    ClientInformationService,
    ClientContractService,
    LOSTalkerTitleService,
    EditConfigGuard,
    EditGenericEndpointsGuard,
    EditDefaultConfigGuard,
    EditBaseMappingGuard,
    IntegrationRunTimeService,
    IntegrationPushBackService,
    LosTokenService,
    CustomSQLQueryService,
    IntegrationMaintenanceService,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule { }
